<template>
	<div class="edit-row edit-row--dark">
		<field-regular
			:label="t('popup.editors.label.name')"
			:placeholder="t('sidebar.nodeDesign.placeholder.point')"
			:label-inline="true"
			:field-item-grow="true"
			:field-item-classname="'mod--edit-bar'"
			:return-null-if-empty="false"
			:disabled="isQuickEditBarContentTracked"
			:formatting-shortcuts="true"
			:assist="{content: t('quickEditBar.titleShortcuts'), direction: 'bottom'}"
			@on-change="updateLang"
			@format-shortcut="onFormatShortcut"
			v-model="nodeName"
		></field-regular>
		<div class="edit-control">
			<div
				@click="boldToggle"
				:class="[boldAvailableClassname, boldActiveClassname, italicActiveNoBoldClassname]"
				class="edit-control__item"
			>
        <i :class="boldIconClassname" class="fas fa-bold"></i>
        <div v-if="!isBoldAvailable && !italicActiveNoBoldClassname" class="tooltip__item top">{{ t('quickEditBar.tooltips.noBold') }}</div>
				<div v-if="italicActiveNoBoldClassname" class="tooltip__item top">{{ t('quickEditBar.tooltips.noBoldItalic') }}</div>
			</div>
			<div
				@click="italicToggle"
				:class="[italicAvailableClassname, italicActiveClassname, boldActiveNoItalicClassname]"
				class="edit-control__item"
			>
        <i :class="italicIconClassname" class="fas fa-italic"></i>
				<div v-if="!isItalicAvailable && !boldActiveNoItalicClassname" class="tooltip__item top">{{ t('quickEditBar.tooltips.noItalic') }}</div>
        <div v-if="boldActiveNoItalicClassname" class="tooltip__item top">{{ t('quickEditBar.tooltips.noBoldItalic') }}</div>
			</div>
		</div>
	</div>
</template>

<script>
import FieldRegular from '@/components/fields/FieldRegular';
import {computed} from 'vue';
import {useI18n} from 'vue-i18n';
import {useStore} from 'vuex';
import useTranslations from "@/utils/language/useTranslations";
export default {
	components: {
		FieldRegular
	},
	setup() {
		// base
		const { t } = useI18n();
		const store = useStore();
		
		// store data/actions
		const currentNode = computed(() => store.getters['nodes/getSelectedNode']);
		const nodeId = computed(() => currentNode.value.id);
    
    // font
    const fonts = computed(() => store.getters['fonts/getFontsCollected']);
    const font = computed(() => fonts.value[currentNode.value.fontFamily] || null);
    const fontProperties = computed(() => font.value ? font.value.properties : []);
    
    // bold
		const isBoldAvailable = computed(() => !!fontProperties.value['Bold']);
    const boldAvailableClassname = computed(() => isBoldAvailable.value ? '' : 'tooltip tooltip--centered dark inactive');
    const boldIconClassname = computed(()=> isBoldAvailable.value ? '' : 'opacity--05');
    const isBoldActive = computed(() => currentNode.value.fontWeight === 700);
    const boldActiveClassname = computed(() => isBoldActive.value ? 'active' : '');

    // combinations
    const isBoldItalicAvailable = computed(() => !!fontProperties.value['Bold italic']);
    const boldActiveNoItalicClassname = computed(() => isBoldActive.value && !isBoldItalicAvailable.value ? 'tooltip tooltip--centered dark inactive' : '' );
    const italicActiveNoBoldClassname = computed(() => isItalicActive.value && !isBoldItalicAvailable.value ? 'tooltip tooltip--centered dark inactive' : '');
    function boldToggle(){
      if(!isBoldAvailable.value) return;
      if(isItalicActive.value && !isBoldItalicAvailable.value) return;
      const valueForToggle = fontProperties.value['Normal'] ? 400 : Object.values(fontProperties.value)[0].weight;
			const fontWeight = isBoldActive.value ? valueForToggle : 700;
      nodeDesignUpdate({fontWeight: fontWeight});
      nodeDesignSave({fontWeight: fontWeight});
    }

    // italic
		const isItalicAvailable = computed(() => Object.values(fontProperties.value).some(property => property.style === 'italic'));
    const italicAvailableClassname = computed(() => isItalicAvailable.value ? '' : 'tooltip tooltip--centered dark inactive');
    const italicIconClassname = computed(()=> isItalicAvailable.value ? '' : 'opacity--05');
    const isItalicActive = computed(() => currentNode.value.fontStyle === 'italic');
    const italicActiveClassname = computed(() => isItalicActive.value ? 'active' : '');
    function italicToggle(){
      if(!isItalicAvailable.value) return;
      if(isBoldActive.value && !isBoldItalicAvailable.value) return;
      const fontStyle = isItalicActive.value ? 'normal' : 'italic';
      nodeDesignUpdate({fontStyle: fontStyle});
      nodeDesignSave({fontStyle: fontStyle});
    }

    // Keyboard shortcuts emitted from the title field. Bold/italic reuse the
    // toolbar toggles, so font-availability guards apply automatically.
    function onFormatShortcut(type){
      if(type === 'bold') boldToggle();
      if(type === 'italic') italicToggle();
    }
		
    // store actions
		function nodeUpdateContent(content, id){
			if(id === undefined) id = currentNode.value.id;
			store.dispatch('nodes/content/nodeUpdateContent', {id, content});
		}
		async function nodeSaveContent(content, id){
			if(id === undefined) id = currentNode.value.id;
			await store.dispatch('nodes/content/nodeSaveContent', {id, content});
		}
    async function nodeDesignUpdate(designData){
      const id = currentNode.value.id;
      await store.dispatch('nodes/design/nodeDesignUpdate', {id, designData});
    }
    async function nodeDesignSave(designData){
      const id = currentNode.value.id;
      await store.dispatch('nodes/design/nodeDesignSave', {id, designData});
    }

		// language
		const language = computed(() => store.getters["maps/languages/getLanguage"].id);
		const currentLang = computed(() => store.getters["maps/languages/getLanguageLocal"].id);
		const nodeOriginalActive = computed(() => store.getters["nodes/getSelectedRawNode"]);
		const nodesOriginal = computed(() => store.getters["nodes/getRawNodes"]);
		const nodeActiveID = nodeOriginalActive.value.id;
		const {composeIntoLang, getSubvariants} = useTranslations();
		function updateLang(value){
			if(!value.trim()) return;
			const nodeOriginal = nodesOriginal.value.find(node => node.id === nodeActiveID);
			if(currentLang.value === language.value) {
				// update title in additional languages
				const result = composeIntoLang({value, field: 'title', currentLang: currentLang.value, langObj: nodeOriginal.content.lang})
				// default language is a part of the result, thus ensure the length of lang object is more than 1 before making request
				if(Object.keys(result.lang).length > 1){
					nodeUpdateContent(result, nodeActiveID);
					nodeSaveContent(result, nodeActiveID);
				}
				return;
			}
			// if the current lang isn't a default language,
			// check if there are subvariants of the current language and update them accordingly
			const subvariants = getSubvariants({id: currentLang.value, lang: nodeOriginal.content.lang});
			if(subvariants.length && currentLang.value !== language.value){
				const result = {lang: {[currentLang.value]: {title: value}}}
				subvariants.forEach(lang => {
					if(!nodeOriginal.content.lang[lang]['title'] && !nodeOriginal.content.lang[lang].translated){
						Object.assign(result.lang, {[lang]: {title: value}})
					}
				})
				nodeUpdateContent(result, nodeActiveID);
				nodeSaveContent(result, nodeActiveID);
			}
			
		}
		
		// edit-bar content status
		const isQuickEditBarContentTracked = computed(() => store.getters['nodes/content/getQuickEditBarContentTrackedChanges']);
		function updateQuickEditBarContentWarning(status){
			store.dispatch('nodes/content/updateQuickEditBarContentWarning', status);
		}
		
		// component actions
		const nodeName = computed({
			get(){return currentNode.value?.title},
			set(value){
				const result = {lang: {[currentLang.value]: {title: value}}};
				nodeUpdateContent(result);
				nodeSaveContent(result);
			}
		})
		
		return {
			t,
			isQuickEditBarContentTracked,
			nodeName,
			nodeId,
			isBoldAvailable,
			boldAvailableClassname,
      boldIconClassname,
			isBoldActive,
			boldActiveClassname,
			isItalicAvailable,
      italicAvailableClassname,
      italicIconClassname,
      italicActiveClassname,
			isItalicActive,
      boldActiveNoItalicClassname,
			italicActiveNoBoldClassname,
      boldToggle,
			italicToggle,
			onFormatShortcut,
			updateLang,
		}
	}
}
</script>

<style lang="scss">
@use "@/scss/sections/editor/quick-edit-bar/edit-control";
</style>
